import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams, useHistory } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import axios from 'axios'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Button } from '../../ui/button'
import { Checkbox } from '../../ui/checkbox'
import { Label } from '../../ui/label'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'
import WaitlistButton from '../../leevo_ui/WaitlistButton/WaitlistButton'
import { requestCreator } from '../../../utilities/requests'
import useAuth from '../../../utilities/hooks/useAuth'
import { cn } from '../../../lib/utils'
import { studentDisplayName } from '../../../utilities/studentNames'

const { get: getStudents, cancel: cancelStudentsRequest } = requestCreator()

function classLabelForOption(klass, fallbackIndex) {
  const dayOfWeek = klass.dayOfWeek || klass.day_of_week
  const timeOfDay = klass.timeOfDay || klass.time_of_day
  if (dayOfWeek && timeOfDay) return `${dayOfWeek} ${timeOfDay}`
  if (dayOfWeek) return dayOfWeek
  if (timeOfDay) return timeOfDay
  return `Class ${fallbackIndex + 1}`
}

function classOptionsForPackage(klassPackage) {
  const klasses = Array.isArray(klassPackage.klasses) ? klassPackage.klasses : []
  if (klasses.length > 0) {
    return klasses.map((klass, index) => ({
      id: klass.id,
      label: classLabelForOption(klass, index)
    }))
  }

  const klassIds = klassPackage.klassIds || klassPackage.klass_ids || []
  return klassIds.map((klassId, index) => ({
    id: klassId,
    label: `Class ${index + 1}`
  }))
}

function SelectKlasses({ studentsList = [], onKlassesSelected, checkoutError = '' }) {
  const [isLoading, setIsLoading] = useState(true)
  const [students, setStudents] = useState([])
  const { currentUser } = useAuth()
  const { facility_id } = useParams()
  const history = useHistory()

  const {
    handleSubmit,
    control,
    watch,
    formState: { isSubmitting }
  } = useForm({
    defaultValues: getInitialValues()
  })
  const selectedPackages = watch()
  const hasSelectedPackage = students.some((student) =>
    student.availableKlassPackages?.some((klassPackage) => {
      const selectedPackageIds = selectedPackages[`user-${student.id}-klasses`] || []
      const spotsLeft = klassPackage.spotsLeftForStudents?.[student.id]
      return selectedPackageIds.includes(klassPackage.id) && (!isFinite(spotsLeft) || spotsLeft > 0)
    })
  )
  const hasSelectablePackage = students.some((student) =>
    student.availableKlassPackages?.some((klassPackage) => {
      const spotsLeft = klassPackage.spotsLeftForStudents?.[student.id]
      return !isFinite(spotsLeft) || spotsLeft > 0
    })
  )

  function getInitialValues() {
    return studentsList.reduce((acc, student) => {
      const selected_packages = student.selectedKlassPackages || []
      acc[`user-${student.id}-klasses`] = selected_packages.map((pkg) => pkg.id)
      return acc
    }, {})
  }

  useEffect(() => {
    getStudents(`/api/guardians/${currentUser.id}/students`, {
      params: {
        include:
          'current_skill_level,available_klass_packages,available_klass_packages.klasses,fully_enrolled_klass_packages',
        facility_id
      }
    })
      .then((students) => {
        if (students.length == 0) history.replace(`/facilities/${facility_id}/register`)
        setStudents(
          [...students].map((student) => {
            return {
              ...student,
              selectedKlassPackages: studentsList.find((s) => s.id === student.id)?.selectedKlassPackages || []
            }
          })
        )
        setIsLoading(false)
      })
      .catch((err) => {
        if (!axios.isCancel(err)) history.replace('/500')
      })
    return cancelStudentsRequest
  }, [])

  function onSubmit(values) {
    const convertedValues = {}
    Object.keys(values).forEach((key) => {
      const [, studentId] = key.split('-')
      const selectedIds = values[key] || []
      const student = students.find((s) => s.id.toString() === studentId)
      if (student) {
        convertedValues[studentId] = student.availableKlassPackages
          .filter((pkg) => selectedIds.includes(pkg.id))
          .map(({ id, title }) => ({ id, title }))
      }
    })
    onKlassesSelected(convertedValues)
  }

  const sortedStudents = students
    .slice()
    .sort((a, b) => studentDisplayName(a).localeCompare(studentDisplayName(b), 'en', { sensitivity: 'base' }))

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {isLoading ? (
        <LoadingSpinner size="12" className="flex items-center justify-center my-10" />
      ) : (
        <>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Select Your Classes</CardTitle>
              <CardDescription>Choose classes for each student below</CardDescription>
            </CardHeader>
          </Card>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {sortedStudents.map(
              ({
                id,
                fullName,
                preferredName,
                currentSkillLevel,
                availableKlassPackages,
                fullyEnrolledKlassPackages
              }) => (
                <Card key={id}>
                  <CardHeader className="bg-gray-50">
                    <CardTitle className="text-lg">
                      {studentDisplayName({ fullName, preferredName })} - {currentSkillLevel?.name || 'No skill level'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-6">
                    {availableKlassPackages.length === 0 && fullyEnrolledKlassPackages.length === 0 && (
                      <p className="text-sm text-gray-600" data-cy="no-classes-notification">
                        Registration is not currently open for {currentSkillLevel?.name || 'this skill level'}. Please
                        contact the facility if you expected to see a class.
                      </p>
                    )}

                    {availableKlassPackages.length > 0 && (
                      <Controller
                        name={`user-${id}-klasses`}
                        control={control}
                        render={({ field }) => (
                          <div className="space-y-3">
                            {availableKlassPackages.map((klassPackage) => {
                              const spotsLeft = klassPackage.spotsLeftForStudents?.[id]
                              const isDisabled = isFinite(spotsLeft) && spotsLeft <= 0
                              const isSelected = field.value?.includes(klassPackage.id) || false
                              const classOptions = classOptionsForPackage(klassPackage)

                              return (
                                <div
                                  key={klassPackage.id}
                                  className={cn(
                                    'flex items-start gap-3 p-4 border rounded-lg transition-colors',
                                    isSelected
                                      ? 'border-purple-600 bg-purple-50'
                                      : 'border-gray-200 hover:border-gray-300'
                                  )}
                                >
                                  <Checkbox
                                    id={`klass-${id}-${klassPackage.id}`}
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onCheckedChange={(checked) => {
                                      const currentValue = field.value || []
                                      if (checked) {
                                        field.onChange([...currentValue, klassPackage.id])
                                      } else {
                                        field.onChange(currentValue.filter((pkgId) => pkgId !== klassPackage.id))
                                      }
                                    }}
                                  />
                                  <div className="flex-1">
                                    <Label
                                      htmlFor={`klass-${id}-${klassPackage.id}`}
                                      className="font-semibold text-gray-900 cursor-pointer"
                                    >
                                      {klassPackage.title}
                                    </Label>
                                    {klassPackage.description && (
                                      <p className="text-sm text-gray-600 mt-1">{klassPackage.description}</p>
                                    )}
                                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-700">
                                      {klassPackage.remainingKlassDaysForStudents?.[id] && (
                                        <span>{klassPackage.remainingKlassDaysForStudents[id]} classes</span>
                                      )}
                                      {isFinite(spotsLeft) && (
                                        <span className={spotsLeft <= 5 ? 'text-orange-600 font-medium' : ''}>
                                          {spotsLeft} {spotsLeft === 1 ? 'spot' : 'spots'} left
                                        </span>
                                      )}
                                      {klassPackage.priceInDollarsForStudents?.[id] && (
                                        <span className="font-semibold text-purple-600">
                                          ${klassPackage.priceInDollarsForStudents[id]}
                                        </span>
                                      )}
                                    </div>
                                    {isDisabled && (
                                      <div className="mt-2 space-y-2">
                                        <p className="text-sm text-red-600 text-balance">This class is full</p>
                                        {classOptions.length > 0 ? (
                                          <WaitlistButton
                                            classOptions={classOptions}
                                            studentId={id}
                                            facilityId={facility_id}
                                          />
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      />
                    )}

                    {fullyEnrolledKlassPackages.length > 0 && (
                      <div className="mt-6 pt-6 border-t">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Already Enrolled</h4>
                        <div className="space-y-3">
                          {fullyEnrolledKlassPackages.map((klassPackage) => (
                            <div key={klassPackage.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                              <div className="font-semibold text-gray-900">{klassPackage.title}</div>
                              {klassPackage.description && (
                                <p className="text-sm text-gray-600 mt-1">{klassPackage.description}</p>
                              )}
                              <p className="text-sm text-gray-600 mt-2">
                                This student is already enrolled in all classes for this package.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            )}

            <div className="space-y-3 pt-6">
              {!hasSelectablePackage && (
                <div className="p-4 rounded-md border border-amber-200 bg-amber-50" role="status">
                  <p className="text-sm text-amber-900">
                    No classes are currently available for these students. Please contact the facility for assistance.
                  </p>
                </div>
              )}
              {checkoutError && (
                <div className="p-4 rounded-md border border-red-200 bg-red-50" role="alert">
                  <p className="text-sm text-red-800">{checkoutError}</p>
                </div>
              )}
              {hasSelectablePackage && !hasSelectedPackage && (
                <p className="text-sm text-gray-600 text-right">Select at least one class to continue.</p>
              )}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isSubmitting || !hasSelectedPackage}
                  className="bg-purple-600 hover:bg-purple-700 px-8"
                  size="lg"
                >
                  {isSubmitting ? 'Loading...' : 'Continue'}
                </Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  )
}

SelectKlasses.propTypes = {
  studentsList: PropTypes.array,
  onKlassesSelected: PropTypes.func.isRequired,
  checkoutError: PropTypes.string
}

export default SelectKlasses
